import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { fuseAnimations } from '@fuse/animations';

import { UserSettingService } from 'app/shared/services/user-setting.service';
import { MessageBoxComponent, MessageBoxParameters, MessageBoxType } from '../messageBox/messageBox.component';
import { DxDataGridComponent } from 'devextreme-angular';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs/dist/exceljs';
import saveAs from 'file-saver';
import dxDataGrid from 'devextreme/ui/data_grid';
import { enumReviewStatus } from 'app/shared/models/review';
import { DateFormatPipe } from 'app/pipes/date-format.pipe';
import DataGrid from 'devextreme/ui/data_grid';
import { DateHelper } from 'app/main/content/misc/date.module';

(DataGrid as any).registerModule('columnChooserSorting', {
  extenders: {
    controllers: {
      columns: {
        getChooserColumns: function (loadAllColumns) {
          const result = this.callBase(loadAllColumns);
          return result.sort((column1, column2) => column1.caption.localeCompare(column2.caption));
        },
      },
    },
  },
});

@Component({
  selector: 'data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss'],
  animations: fuseAnimations,
})
export class DataGridComponent implements OnInit, OnDestroy {

  // searchTermEntered: ReplaySubject<string> = new ReplaySubject<string>(1, 100);
  // onSearchTermEntered$: Subscription;

  // used to store columns and data
  _rows: any;
  _columns: any[];
  // shadowRows: any;

  // keep track of currently selected link
  selectedLink: any;

  loading = false;
  valueColumnName = '';
  showValueSummary = false;
  valueFormat = '##.##';
  showFilterRow = true;
  showHeaderFilter = true;
  expanded = false;

  statusAccepted: number = enumReviewStatus.Accepted;
  statusRejected: number = enumReviewStatus.Rejected;

  @Input() set rows(val: any) {
    if (val) {
      this._rows = [...val];
    }
  }

  getYesNoText(cell) {
    var v: string = cell.value as string;
    if (v && typeof v === 'string') {
      v = v.toLocaleLowerCase().trim();
      if (v == 'true' || v == 'yes') return 'Yes'
      else if (v == 'false' || v == 'no') return 'No'
      else return ' - '
    }
    return " - ";
  }

  @Input() set columns(val: any) {
    if (val && (val.length > 0)) {
      this._columns = val.map((column) => {
        if (column.dataType) {
          column.allowFiltering = true;
          switch (column.dataType.toLowerCase()) {
            case 'default':
              // column.cellTemplate = 'defaultCellTemplate';
              // column.calculateCellValue = "getDefaultTextCellValue";
              break;
            case 'yesno':
              column.dataType = 'yesno';
              column.cellTemplate = 'yesnoCellTemplate';
              break;
            case 'checkbox':
              column.dataType = 'boolean';
              break;
            case 'currency':
              column.dataType = 'number';
              column.cellTemplate = 'currencyCellTemplate';
              column.width = 120;
              break;
            case 'date':
              column.cellTemplate = 'dateCellTemplate';
              column.width = 120;
              break;
            case 'datetime':
              column.cellTemplate = 'dateTimeCellTemplate';
              break;
            case 'link':
              column.cellTemplate = 'linkCellTemplate'
              column.dataType = 'string';
              //DRJ - SL changed the dataType to string to solve the issue with DevExpress search panel not searching the custom data type of link.
              //from the documentation, its due to the search panel knowing how to handle that, so setting it as a string means we dont have to deal with it.
              break;
            case 'markericon':
              column.cellTemplate = 'markerIconCellTemplate';
              column.width = 100;
              break;
            case 'markers':
              column.cellTemplate = 'markersCellTemplate';
              column.groupValue = this.groupByMarkers;
              column.allowFiltering = false;
              break;
            case 'reviewstatus':
              column.cellTemplate = 'reviewStatusCellTemplate';
              break;
            case 'assessmentscore':
              column.cellTemplate = 'assessmentCellTemplate';
              break;
            default:
              break;
          }
        }
        return column;
      });

      // replaced guessing of value column with prop on column - look for first column with prop and assume that is the relevant value column
      const valColumn = this._columns.find((i) => i.showSummary);
      if (valColumn) {
        this.showValueSummary = true;
        this.valueColumnName = valColumn.name;
        this.valueFormat = (valColumn.dataType && (valColumn.dataType.toLowerCase() === 'currency')) ? '£###0.00' : '##.##';
      }
      else {
        this.showValueSummary = false;
      }

      setTimeout(() => {
        // any columns marked as false should be removed from the UI, irrespective of whether we have existing state or not
        this._columns.filter((i) => (i.visible === ColumnVisibility.False)).forEach((i) => {
          this.setColumnVisibility(i.prop, i.visible);
        });
      }, 300);
    }
  }

  @Input() allowCustomisation = true;
  @Input() allowDelete = true;
  @Input() allowEdit = true;
  @Input() detailTemplate: string;
  @Input() keyProperty: string;
  @Input() showButtons = true;
  @Input() showPager = true;
  @Input() showRowCount = true;
  @Input() showSearch = true;
  @Input() showSelection = false;
  @Input() storageKey: string;
  @Input() selectionMode = 'single';
  @Input() set showLoading(val: boolean) {
    // MRD - note to self - we wrap the calls in setTimeout as it is entirely possible that
    // the request to start and end custom loading happens before the grid instance becomes available
    if (val) {
      this.loading = true;
      setTimeout(() => {
        if (this.loading) {
          this.dataGrid.instance.beginCustomLoading('Loading...');
        }
      }, 200);
    }
    else {
      if (this.dataGrid && this.dataGrid.instance) {
        this.loading = false;
        this.dataGrid.instance.endCustomLoading();
      }
      else {
        setTimeout(() => {
          this.loading = false;
          if (this.dataGrid && this.dataGrid.instance) {
            this.dataGrid.instance.endCustomLoading();
          }
        }, 300);
      }
    }
  }

  @Output() onDelete: EventEmitter<any> = new EventEmitter();
  @Output() onEdit: EventEmitter<any> = new EventEmitter();
  @Output() onOpenLink: EventEmitter<any> = new EventEmitter();
  @Output() onSelectionChanged: EventEmitter<any> = new EventEmitter();
  @Output() onRowUpdated: EventEmitter<any> = new EventEmitter();
  @Output() onFilterVisibilityChanged: EventEmitter<any> = new EventEmitter();

  @ViewChild(DxDataGridComponent) dataGrid: DxDataGridComponent;

  constructor(public dialog: MatDialog, private router: Router, private userSettingService: UserSettingService, private dateHelper: DateHelper) {
    // this.onSearchTermEntered$ = this.searchTermEntered.pipe(debounceTime(200)).subscribe((searchTerm) => {
    //   this.doSearch(searchTerm);
    // });
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.saveState(this.dataGrid.instance.state());
  }

  get instance(): dxDataGrid {
    return this.dataGrid.instance;
  }

  loadState = () => {
    if (!this.storageKey) {
      console.log('No storage key!!');
      this.setFilterVisibility(false);
    }
    else {
      if (this.allowCustomisation) {
        const listSettings = this.userSettingService.getSetting(this.storageKey, '{}');
        const state = listSettings.value ? JSON.parse(listSettings.value) : undefined;
        // console.log(`Found state for ${this.storageKey}`, state);
        this.setFilterVisibility(state && state.filterPanel && (state.filterPanel.filterEnabled === true));

        setTimeout(() => {
          // console.log('Apply state', state, this.dataGrid);
          if (this._columns) {
            if (!(state && state.columns)) {
              // MRD - setting the visible property of the columns in the html isn't working as advertised - hence the following hack to force visibility to the correct state
              // Note we only need to do this if grid state is not yet available
              this._columns.filter((i) => ((i.visible === ColumnVisibility.False) || (i.visible === ColumnVisibility.Hide))).forEach((i) => {
                this.setColumnVisibility(i.prop, i.visible);
              });
            }

            // // any columns marked as false should be removed from the UI, irrespective of whether we have existing state or not
            this._columns.filter((i) => (i.visible === ColumnVisibility.False)).forEach((i) => {
              this.setColumnVisibility(i.prop, i.visible);
            });
          }
          if (this.dataGrid) {
            const selected = this.dataGrid.selectedRowKeys;
            if (selected && (selected.length > 0) && this.dataGrid.instance) {
              this.dataGrid.instance.navigateToRow(this.dataGrid.selectedRowKeys[0]);
            }
            this.expand((state && state.expanded) || false);
          }
        }, 200);

        return new Promise<any>((resolve) => { resolve(state); });
      }
    }
  }

  saveState = (state) => {
    if (this.allowCustomisation && this.storageKey) {
      if (state) {
        if (state.filterPanel) {
          // make sure filterEnabled correctlly saved as something is screwing this up if left to DevX
          state.filterPanel.filterEnabled = this.showFilterRow;
        }
        state.expanded = this.expanded;
        this.userSettingService.setSetting(this.storageKey, JSON.stringify(state));
      }
    }
  }

  // MRD - Note to self:  We wrap emitted events in setTimeout.  This means the state of the grid can be updated before it is destroyed (and saved)

  edit(row) {
    setTimeout(() => { this.dataGrid.instance.hideColumnChooser(); this.onEdit.emit(row); });
  }

  delete(row) {
    this.dataGrid.instance.hideColumnChooser();
    const info = new MessageBoxParameters('Are you sure you want to delete this record?', 'Confirm', MessageBoxType.Confirm, true);
    this.dialog.open(MessageBoxComponent, { data: info, disableClose: false, autoFocus: false }).afterClosed().subscribe((result) => {
      if (result === 'confirm') {
        this.onDelete.emit(row);
      }
    });
  }

  selectLink(row) {
    setTimeout(() => { this.selectedLink = row; });
  }

  openLink(link) {
    setTimeout(() => { this.onOpenLink.emit(link); });
  }

  setFilterVisibility(value: boolean) {
    setTimeout(() => { this.showFilterRow = value; });
    setTimeout(() => { this.showHeaderFilter = value; });
    setTimeout(() => { this.onFilterVisibilityChanged.emit(value); });
  }

  selectionChanged(e) {
    this.onSelectionChanged.emit(e);
  }

  rowUpdated(e) {
    this.onRowUpdated.emit(e);
  }

  onEditorPreparing(e) {
    if (e.parentType === 'filterRow') {
      switch (e.dataType.toLowerCase()) {
        case 'date': e.editorOptions.displayFormat = 'dd/MMM/yyyy'; break;
        case 'datetime': e.editorOptions.displayFormat = 'dd/MMM/yyyy HH:mm:ss'; break;
        default: break;
      }
    }
    if (e.parentType === 'dataRow') {
      if (e.dataType === 'boolean') {
        // make sure any indeterminate boolean values are displayed as false
        e.editorOptions.value = (e.value === true);
        // add hint to help with web accessibility
        e.editorOptions.hint = e.caption;
      }
      if (!e.row.data.canEdit) {
        e.editorOptions.disabled = true;
      }
    }
  }

  onCellPrepared(e) {
    switch (e.rowType) {
      case 'header':
        if (e.column.dataField === 'id') {
          e.cellElement.classList.add('hide');
        }
        if (e.column.dataType !== 'boolean' || e.column.dataType !== 'yesno') {
          e.cellElement.classList.add('text-left');
        }
        break;
      case 'data':
        let linkedMarker;
        if (e.data.subjectLinks && e.data.subjectLinks.length > 0) {
          linkedMarker = e.data.subjectLinks.find((i) => i.linkedSubject && i.linkedSubject.subjectMarkers && i.linkedSubject.subjectMarkers.length > 0);
        }
        if ((e.data.subjectMarkers && e.data.subjectMarkers.length > 0) || (linkedMarker ? true : false)) {
          e.cellElement.classList.add('marker');
        }
        if (e.column.dataType !== 'boolean' || e.column.dataType !== 'yesno') {
          e.cellElement.classList.add('text-left');
        }
        if (e.column.dataType === 'date') {
          if (e.value) {
            e.value = this.dateHelper.utc(e.value);
          }
        }
        break;
      case 'group':
        if (e.column.dataType !== 'boolean' || e.column.dataType !== 'yesno') {
          e.cellElement.classList.add('text-left-child');
        }
        if ((e.column.dataType === 'date') || (e.column.dataType === 'datetime')) {
          if (e.values && (e.values.length > 0)) {
            const val: string = this.dateHelper.getDateAsString(e.values[0]);
            if (val !== 'Invalid Date') e.values[0] = val;
          }
        }
        break;
      default:
        break;
    }
  }

  groupByMarkers(rowData) {
    const markers = rowData.subjectMarkers;
    return (markers && (markers.length > 0)) ? markers.map((i) => i.marker.name).join(', ') : '';
  }

  onRowDblClick(e) {
    this.edit(e.data);
  }

  public toggleFilterRow() {
    this.dataGrid.instance.clearFilter();
    this.setFilterVisibility(!this.showFilterRow);
  }

  public export(saveAsFileName) {

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');
    exportDataGrid({
      component: this.dataGrid.instance,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === 'data') {
          if (gridCell?.column?.dataType === 'date') {
            if (gridCell.value) {
              // MRD - although we have specified a date type the gridCell.value is not always a date value
              // this leads to errors if left to DevX so to get around this we use our DateFormatPipe to convert into correct DD/MMM/YYYY string
              // and then convert back to an appropriate date object
              excelCell.value = this.dateHelper.utcDate(new DateFormatPipe('en-GB').transform(gridCell.value));
            }
            excelCell.numFmt = 'dd/mmm/yyyy';
          }
          if (gridCell?.column?.dataType?.toLowerCase() === 'currency') {
            excelCell.numFmt = '£#,##0.00;[Red]-£#,##0.00';
          }
          if (gridCell?.column?.dataField === 'subjectMarkers') {
            excelCell.value = (gridCell.value.length > 0) ? gridCell.value.map((i) => i.marker.name).join(', ') : '';
          }

          if (gridCell?.column?.dataField === 'reviewStatusName') {
            let colour;
            switch (gridCell.value.toLowerCase()) {
              case 'rejected':
                colour = 'FF0000';
                break;
              case 'accepted':
                colour = '008000';
                break;
              default:
                colour = 'FFA500';
                break;
            }
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colour } };
          }

          if (gridCell?.column?.dataField === 'assessmentScore') {
            if (gridCell.value) {
              excelCell.value = `${gridCell.value} / ${gridCell.data.assessmentThreshold}`;
              let colour = '';
              switch (gridCell.data.assessmentReviewStatus) {
                case enumReviewStatus.Rejected:
                  colour = 'FF0000';
                  break;
                case enumReviewStatus.Accepted:
                  colour = '008000';
                  break;
                default:
                  colour = '0277BD';
                  break;
              }
              excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colour } };
            }
            else {
              excelCell.value = '';
            }
          }
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), saveAsFileName);
      });
    });
  }

  public reset() {
    // MRD - in certain scenarios the various function calls only partially work and it is necessary to re-run
    // therefore I have enclosed in a loop to make sure the code is run twice automatically
    for (let x = 0; x < 2; x++) {
      try {
        this.dataGrid.instance.beginUpdate();
        this.dataGrid.instance.clearFilter();
        this.dataGrid.instance.clearGrouping();
        this.dataGrid.instance.clearSelection();
        this.dataGrid.instance.clearSorting();
        this.dataGrid.instance.pageSize(this.showPager ? 10 : 0);
        this.dataGrid.instance.resetOption('focusedRowIndex');
        this.setFilterVisibility(false);
        const colCount = this.dataGrid.instance.columnCount();
        for (let i = 0; i < colCount; i++) {
          this.dataGrid.instance.columnOption(i, 'visible', true);
          this.dataGrid.instance.columnOption(i, 'selectedFilterOperation', undefined);
        }
        for (let i = this._columns.length - 1; i > -1; i--) {
          this.dataGrid.instance.columnOption(this._columns[i].name, 'visibleIndex', this.showButtons ? 1 : 0);
        }
        this.dataGrid.focusedRowKey = this._rows[0].id;

        this.expand(false);

        // MRD - setting the visible property of the columns in the html isn't working as advertised - hence the following hack to force visibility to the correct state
        this._columns.filter((i) => ((i.visible === ColumnVisibility.False) || (i.visible === ColumnVisibility.Hide))).forEach((i) => {
          this.setColumnVisibility(i.prop, i.visible);
        });

        // navigate to the first row
        setTimeout(() => { this.dataGrid.instance.navigateToRow(this._rows[0].id); });
      }
      finally {
        this.dataGrid.instance.endUpdate();
      }
    }
  }

  expand(expanded: boolean) {
    this.expanded = expanded;
    if (expanded) {
      this.dataGrid.instance.expandAll(-1);
    }
    else {
      this.dataGrid.instance.collapseAll(-1);
    }
  }

  setColumnVisibility(column, visible: ColumnVisibility) {
    if (column && this.dataGrid && this.dataGrid.instance) {
      switch (visible) {
        case ColumnVisibility.Hide:
          // initially hide
          this.dataGrid.instance.columnOption(column, 'visible', false);
          break;
        case ColumnVisibility.False:
          // hide and never make available
          this.dataGrid.instance.columnOption(column, 'visible', false);
          this.dataGrid.instance.columnOption(column, 'showInColumnChooser', false);
          break;
        default:
          break;
      }
    }
  }
}

export enum ColumnVisibility { True, False, Hide }
